import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = params.get('token') || '';
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setStatus('error');
      setError('Verification token is missing from the link.');
      return;
    }

    (async () => {
      try {
        const resp = await fetch('https://api.glowytics.com/api/auth/verify-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token })
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          setStatus('error');
          setError(data.error || 'Verification failed. The link may be expired or already used.');
          return;
        }
        if (data.user) login(data.user);
        setStatus('success');
        setTimeout(() => navigate('/'), 1500);
      } catch (e) {
        setStatus('error');
        setError(e.message || 'Network error. Please try again.');
      }
    })();
  }, [token, navigate, login]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center">
        <img
          src="/Calisma-Yuzeyi-20@5x-4.png"
          alt="Glowytics"
          className="h-10 mx-auto mb-8"
        />

        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Verifying your email…</h1>
            <p className="text-gray-600 text-sm">This should only take a second.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Email verified!</h1>
            <p className="text-gray-600 text-sm">Redirecting you to your dashboard…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Verification failed</h1>
            <p className="text-gray-600 text-sm mb-6">{error}</p>
            <div className="flex flex-col gap-3">
              <a
                href="/login"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Back to login
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
